import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useGetDiscountCodes, useToggleDiscountCode } from '../api/discountAdmin';
import { DiscountCode } from '../utils/types';
import { AlertCircle, Tag } from 'lucide-react';
import CreateDiscountModal from '../components/admin/CreateDiscountModal';
import './DiscountAdmin.css';

const formatValue = (code: DiscountCode): string => {
  if (code.discount_type === 'percentage') {
    return `${code.discount_value}%`;
  }
  return `$${code.discount_value.toFixed(2)}`;
};

const formatDate = (iso: string): string => {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const DiscountAdmin: React.FC = () => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());

  const { data, isLoading, error } = useGetDiscountCodes();
  const toggleDiscountCode = useToggleDiscountCode();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleToggle = async (code: DiscountCode) => {
    setTogglingIds((prev) => new Set(prev).add(code.id));
    try {
      await toggleDiscountCode.mutateAsync({ id: code.id, is_active: !code.is_active });
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(code.id);
        return next;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="admin-loading">
            <div className="loading-spinner"></div>
            <p>Loading discount codes...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="admin-error">
            <AlertCircle className="icon icon--xl" />
            <h2>Error Loading Discount Codes</h2>
            <p>Unable to load discount codes. Please try again.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const codes = data?.discount_codes ?? [];

  return (
    <div className="app">
      <Header />

      <main className="main-content">
        <div className="admin-container">
          <div className="admin-header discount-admin__header">
            <div>
              <h1 className="admin-title">Discount Codes</h1>
              <p className="admin-subtitle">{codes.length} {codes.length === 1 ? 'code' : 'codes'}</p>
            </div>
            <button className="btn btn--primary btn--md" onClick={() => setCreateModalOpen(true)}>
              <Tag className="icon icon--xs" />
              New Code
            </button>
          </div>

          {codes.length === 0 ? (
            <div className="discount-admin__empty">
              <Tag size={32} />
              <p>No discount codes yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="discount-admin__table-wrap">
              <table className="discount-admin__table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Type</th>
                    <th>Value</th>
                    <th>Camp</th>
                    <th>Single use</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((code) => (
                    <tr key={code.id} className={!code.is_active ? 'discount-admin__row--inactive' : ''}>
                      <td>
                        <span className="discount-admin__code">{code.code}</span>
                      </td>
                      <td className="discount-admin__type">
                        {code.discount_type === 'percentage' ? 'Percentage' : 'Fixed'}
                      </td>
                      <td className="discount-admin__value">{formatValue(code)}</td>
                      <td className="discount-admin__camp">
                        {code.camp_id != null ? (
                          <span className="discount-admin__camp-id">#{code.camp_id}</span>
                        ) : (
                          <span className="discount-admin__global">Global</span>
                        )}
                      </td>
                      <td className="discount-admin__single-use">
                        {code.is_single_use ? 'Yes' : 'No'}
                      </td>
                      <td>
                        <span className={`discount-admin__badge ${code.is_active ? 'discount-admin__badge--active' : 'discount-admin__badge--disabled'}`}>
                          {code.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="discount-admin__date">{formatDate(code.created_at)}</td>
                      <td>
                        <button
                          className={`btn btn--sm ${code.is_active ? 'btn--warning' : 'btn--success'}`}
                          onClick={() => handleToggle(code)}
                          disabled={togglingIds.has(code.id)}
                        >
                          {togglingIds.has(code.id)
                            ? '...'
                            : code.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {createModalOpen && (
        <CreateDiscountModal onClose={() => setCreateModalOpen(false)} />
      )}

      <Footer />
    </div>
  );
};

export default DiscountAdmin;
